import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { ConfirmDialogProps } from '@/components/feedback/ConfirmDialog';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

export const CONFIRM_DELETE_DIALOG_FILE_FRAGMENT = graphql(`
  fragment confirmDeleteDialogFileFields on File {
    _id
    filename
    folder
  }
`);

export const DELETE_FILE_MUTATION = graphql(`
  mutation DeleteFile($data: DeleteFileInput) {
    deleteFile(params: $data) {
      success
    }
  }
`);

interface Props extends Omit<ConfirmDialogProps, 'onConfirm'> {
  file: DocumentType<typeof CONFIRM_DELETE_DIALOG_FILE_FRAGMENT>;
}

export const ConfirmFileDeleteDialog = ({ file, open, onClose }: Props) => {
  const { _id: fileId, filename, folder } = file;

  const [deleteFile, { loading }] = useMutation(DELETE_FILE_MUTATION, {
    onCompleted: onClose,
    optimisticResponse: {
      deleteFile: {
        __typename: 'DeleteFileResult',
        success: true,
      },
    },
    update(cache, { data }) {
      if (!data?.deleteFile?.success) {
        return;
      }

      const normalizedId = cache.identify({
        _id: fileId ?? null,
        __typename: 'File',
      });
      cache.evict({ id: normalizedId });
      cache.gc();
    },
  });

  return (
    <ConfirmDialog
      ariaLabel="delete-file"
      titleText={`Delete this ${folder ? 'folder' : 'file'}?`}
      innerText={
        <>
          Are you sure you want to delete the {folder ? 'folder' : 'file'}{' '}
          <strong>{filename}</strong>
        </>
      }
      confirmText="Delete"
      confirmButtonProps={{ color: 'error' }}
      loading={loading}
      open={open}
      onConfirm={() => {
        deleteFile({
          variables: { data: { file: fileId } },
        });
      }}
      onClose={onClose}
    />
  );
};
